import {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
} from "react";
import { useSelector } from "react-redux";
import { useSocket } from "../SocketContext";
import { useInternalContacts } from "@components/internal-contacts/_hooks/useInternalContacts";
import type { Contact } from "@components/internal-contacts/_types/internalContacts.types";
import { getContactId } from "@components/internal-contacts/_utils/internalContacts.utils";
import { resolveUnifiedContactIdFromEntity } from "@components/internal-contacts/_utils/employeeContacts.utils";
import { selectRole } from "src/redux/features/user/selectors/userState.selectors";
import { useAppSelector } from "src/redux/hooks/redux";
import {
  getSenderDisplayName,
} from "src/notifications/utils/showInternalMessageNotification";
import { emitInternalMessageToast } from "src/notifications/utils/internalMessageToastBus";

interface Entity {
  type?: string;
  uid?: string;
  id?: string | number;
  firstName?: string;
  lastName?: string;
  businessName?: string;
  name?: string;
}

interface MessageRecord {
  uid: string;
  companyId?: number | string | null;
  text?: string;
  content?: {
    text?: string;
    body?: string;
    message?: string;
    name?: string;
    [key: string]: any;
  };
  sender: Entity;
  receiver: Entity;
  createdAt?: string;
  updatedAt?: string;
}

interface UnreadMessagesContextValue {
  getUnreadCount: (contactIdentifier: string | number) => number;
  getTotalUnreadCount: () => number;
  markAsRead: (contact: Contact) => void;
  setActiveContact: (contact: Contact | null) => void;
  refetchContacts: () => void;
  isLoading: boolean;
}

const UnreadMessagesContext = createContext<UnreadMessagesContextValue | null>(null);

const getContactIdentifierFromEntity = (entity: Entity) => {
  if (!entity?.type) return null;
  if (entity.type === "company") return entity.id;
  return entity.uid ?? null;
};

const isEntityMe = (
  entity: Entity,
  { uid, companyIds, workforceUids }: { uid: string; companyIds: (number | string)[]; workforceUids: string[] }
) => {
  if (!entity?.type) return false;
  if (entity.type === "user" && entity.uid === uid) return true;
  if (entity.type === "company" && companyIds.some((id) => Number(id) === Number(entity.id))) return true;
  if (entity.type === "workforce" && entity.uid && workforceUids.includes(entity.uid)) return true;
  return false;
};

const getActiveContactId = (contact: Contact | null) => {
  if (!contact) return null;
  if (contact.type === "company" && contact.id) return contact.id;
  return contact.uid ?? null;
};

const extractMessageText = (msg: MessageRecord): string | null => {
  return (
    msg.text ||
    msg.content?.text ||
    msg.content?.body ||
    msg.content?.message ||
    msg.content?.name ||
    null
  );
};

interface UnreadMessagesProviderProps {
  children: ReactNode;
}

/**
 * UnreadMessagesProvider — single source of truth for unread badges and last-message previews
 * across the app. Wraps the TanStack-Query–backed `useInternalContacts` hook and bridges socket
 * events to keep the cache fresh in real time.
 */
export const UnreadMessagesProvider = ({ children }: UnreadMessagesProviderProps) => {
  const { socket } = useSocket();
  const { uid, company, companies, workforces } = useSelector((state: any) => state.userState);
  const companyId = company?.id;
  const role = useAppSelector(selectRole);

  const workforceUids = useMemo(
    () => workforces?.map((wf: any) => wf.uid).filter(Boolean) || [],
    [workforces]
  );

  const companyIds = useMemo(() => {
    const ids = new Set<string>();

    if (companyId != null) ids.add(String(companyId));
    (companies ?? []).forEach((membership: any) => {
      if (membership?.companyId != null) ids.add(String(membership.companyId));
    });
    (workforces ?? []).forEach((workforce: any) => {
      if (workforce?.companyId != null) ids.add(String(workforce.companyId));
    });

    return Array.from(ids);
  }, [companies, companyId, workforces]);

  const companyIdsSignature = useMemo(() => [...companyIds].sort().join("|"), [companyIds]);

  const companyNames = useMemo(() => {
    const names = new Map<string, string>();

    (companies ?? []).forEach((membership: any) => {
      if (membership?.companyId == null) return;
      const name = membership.company?.businessName || membership.company?.name;
      if (name) names.set(String(membership.companyId), name);
    });

    return names;
  }, [companies]);

  const companyNamesRef = useRef(companyNames);
  companyNamesRef.current = companyNames;

  const identityRef = useRef({ uid, companyId, companyIds, workforceUids, role });
  identityRef.current = { uid, companyId, companyIds, workforceUids, role };

  const activeContactRef = useRef<Contact | null>(null);
  const processedMessageUidsRef = useRef<Set<string>>(new Set());

  const {
    contacts,
    getUnreadCount,
    totalUnread,
    markAsRead: markAsReadMutation,
    incrementUnread,
    updateLastMessage,
    refetch,
    isLoading,
  } = useInternalContacts();

  const contactsRef = useRef(contacts);
  contactsRef.current = contacts;

  const getTotalUnreadCount = useCallback(() => totalUnread, [totalUnread]);

  const markAsRead = useCallback(
    (contact: Contact) => {
      markAsReadMutation(contact);
    },
    [markAsReadMutation],
  );

  const setActiveContact = useCallback(
    (contact: Contact | null) => {
      activeContactRef.current = contact;
      if (contact) markAsRead(contact);
    },
    [markAsRead],
  );

  // Real-time bridge: socket events update the TanStack Query cache so badges and previews
  // stay in sync with the backend's persistent state (no page reload required).
  useEffect(() => {
    if (!socket || !uid) return;

    const handleNewMessage = (messageRecord: MessageRecord) => {
      const messageUid = messageRecord?.uid;
      if (!messageUid || processedMessageUidsRef.current.has(messageUid)) return;

      processedMessageUidsRef.current.add(messageUid);
      if (processedMessageUidsRef.current.size > 100) {
        const first = processedMessageUidsRef.current.values().next().value;
        if (first) processedMessageUidsRef.current.delete(first);
      }

      const {
        uid: myUid,
        companyId: myCompanyId,
        companyIds: myCompanyIds,
        workforceUids: myWorkforceUids,
        role: myRole,
      } = identityRef.current;

      const isForMe = isEntityMe(messageRecord.receiver, {
        uid: myUid,
        companyIds: myCompanyIds,
        workforceUids: myWorkforceUids,
      });
      const isFromMe = isEntityMe(messageRecord.sender, {
        uid: myUid,
        companyIds: myCompanyIds,
        workforceUids: myWorkforceUids,
      });

      // The "other side" of the conversation is the contact whose row we update
      let contactId: string | number | null = null;
      let direction: "incoming" | "outgoing" = "incoming";

      if (isForMe && !isFromMe) {
        contactId =
          resolveUnifiedContactIdFromEntity(messageRecord.sender, contactsRef.current, myRole, myCompanyId) ??
          getContactIdentifierFromEntity(messageRecord.sender);
        direction = "incoming";
      } else if (!isForMe && isFromMe) {
        contactId =
          resolveUnifiedContactIdFromEntity(messageRecord.receiver, contactsRef.current, myRole, myCompanyId) ??
          getContactIdentifierFromEntity(messageRecord.receiver);
        direction = "outgoing";
      }

      if (contactId == null) return;

      const messageCompanyId = messageRecord.companyId;
      const isFromActiveCompany =
        messageCompanyId == null || myCompanyId == null || Number(messageCompanyId) === Number(myCompanyId);

      const hasMatchingContact = contactsRef.current.some(
        (contact) => String(getContactId(contact)) === String(contactId),
      );
      if (!hasMatchingContact && isFromActiveCompany) {
        void refetch();
      }

      const text = extractMessageText(messageRecord);
      const date = messageRecord.createdAt || messageRecord.updatedAt || new Date().toISOString();

      // Always refresh the preview snapshot, even for outgoing or active-chat messages
      if (isFromActiveCompany) {
        updateLastMessage(contactId, {
          lastMessage: text,
          lastMessageAt: date,
          lastMessageDirection: direction,
        });
      }

      // Incoming-only logic
      if (direction !== "incoming") return;

      // If chat is open for this contact, auto-mark as read on the server too
      const activeId = getActiveContactId(activeContactRef.current);
      const isActiveChat = isFromActiveCompany && activeId != null && String(activeId) === String(contactId);

      if (isActiveChat && document.visibilityState === "visible") {
        const activeContact = activeContactRef.current;
        if (activeContact) markAsReadMutation(activeContact);
        return;
      }

      if (isFromActiveCompany) incrementUnread(contactId);

      const matchingContact = contactsRef.current.find(
        (contact) => String(getContactId(contact)) === String(contactId),
      );
      const notificationMessage = matchingContact
        ? {
            ...messageRecord,
            sender: {
              ...messageRecord.sender,
              firstName: messageRecord.sender?.firstName ?? matchingContact.firstName,
              lastName: messageRecord.sender?.lastName ?? matchingContact.lastName,
              businessName:
                messageRecord.sender?.businessName ?? matchingContact.businessName,
              name: messageRecord.sender?.name ?? matchingContact.name,
            },
          }
        : messageRecord;

      emitInternalMessageToast({
        uid: messageUid,
        senderName: getSenderDisplayName(notificationMessage.sender),
        companyId: messageCompanyId ?? null,
        companyName:
          companyNamesRef.current.get(String(messageCompanyId)) ??
          (isFromActiveCompany ? matchingContact?.companyName : null) ??
          null,
        text: text || "",
        contactId,
        contact: isFromActiveCompany ? matchingContact ?? null : null,
      });
    };

    socket.on("messageRecord", handleNewMessage);
    return () => {
      socket.off("messageRecord", handleNewMessage);
    };
  }, [socket, uid, companyIdsSignature, workforceUids, role, incrementUnread, updateLastMessage, markAsReadMutation, refetch]);

  const value = useMemo<UnreadMessagesContextValue>(
    () => ({
      getUnreadCount,
      getTotalUnreadCount,
      markAsRead,
      setActiveContact,
      refetchContacts: refetch,
      isLoading,
    }),
    [getUnreadCount, getTotalUnreadCount, markAsRead, setActiveContact, refetch, isLoading],
  );

  return (
    <UnreadMessagesContext.Provider value={value}>{children}</UnreadMessagesContext.Provider>
  );
};

export const useUnreadMessagesContext = () => {
  const ctx = useContext(UnreadMessagesContext);
  if (!ctx) {
    throw new Error("useUnreadMessagesContext must be used within UnreadMessagesProvider");
  }
  return ctx;
};
